import {safeContributionRecord, contributionMoney, contributionRevision, contributionCurrency, contributionCalendarDate, contributionOptional, contributionPublicRecord, isContributionReceipt, isContributionEvidence, isContributionPayment, isContributionHistory, isContributionAdjustmentDetail, uniqueContributionRows} from './contribution-read-contracts.js';
const isRecord = safeContributionRecord;
const isText = value => typeof value === 'string' && value.length > 0;
const isDate = value => value instanceof Date ? Number.isFinite(value.getTime()) : typeof value === 'string' && Number.isFinite(Date.parse(value));
const isNullableDate = value => value == null || isDate(value);
const isMoney = contributionMoney;
const isCursor = value => value === null || typeof value === 'string' && /^[a-f\d]{24}$/i.test(value);
export class ContributionResponseContractError extends Error {
  constructor(detail) {
    super(`Invalid Treasury contribution response: ${detail}`);
    this.name = 'ContributionResponseContractError';
    this.code = 'INVALID_API_RESPONSE';
  }
}
export class ContributionRecoveryResponseContractError extends Error {
  constructor() {
    super('The earlier contribution action could not be confirmed. Please check again.');
    this.name = 'ContributionRecoveryResponseContractError';
    this.code = 'INVALID_API_RESPONSE';
  }
}
export class ContributionRecoveryContextResponseContractError extends Error {
  constructor() {
    super('Contribution recovery could not verify your current account and community.');
    this.name = 'ContributionRecoveryContextResponseContractError';
    this.code = 'INVALID_API_RESPONSE';
  }
}
export class ContributionActionResponseContractError extends Error {
  constructor(action) {
    super(`Invalid Treasury contribution response for ${action}.`);
    this.name = 'ContributionActionResponseContractError';
    this.code = 'INVALID_API_RESPONSE';
  }
}
const validExternalUrl = value => {try {const url = new URL(value); return typeof value === 'string' && url.protocol === 'https:' && !url.username && !url.password;} catch {return false;}};
const fail = detail => {throw new ContributionResponseContractError(detail);};
const adjustment = row => isContributionAdjustmentDetail(row) && isText(row.id) && ['correction', 'refund', 'clarification'].includes(row.kind)
  && ['proposed', 'approved', 'paid', 'published', 'cancelled'].includes(row.status)
  && (row.message == null || typeof row.message === 'string') && (row.amount === undefined || isMoney(row.amount));
const publicAdjustment = row => contributionPublicRecord(row) && !Object.hasOwn(row, 'id') && !Object.hasOwn(row, 'note') && isContributionAdjustmentDetail(row) && ['refund', 'clarification'].includes(row.kind)
  && ['approved', 'paid', 'published', 'cancelled'].includes(row.status)
  && (row.message == null || typeof row.message === 'string') && (row.amount === undefined || isMoney(row.amount));
const contribution = row => isRecord(row) && isText(row.id) && isText(row.fundId) && isText(row.fundName)
  && isText(row.recipientName) && isText(row.memberName) && contributionCurrency(row.currency) && isMoney(row.amount)
  && typeof row.reference === 'string' && (row.paymentMethod === 'cash' && row.transferredOn === undefined || contributionCalendarDate(row.transferredOn))
  && (row.origin === 'treasurer_recorded' && row.instructions === undefined || typeof row.instructions === 'string')
  && ['awaiting_confirmation', 'confirmed', 'withdrawn', 'superseded'].includes(row.status)
  && isDate(row.createdAt) && contributionRevision(row.revision) && ['member_declaration', 'treasurer_recorded'].includes(row.origin)
  && ['bank_transfer', 'cash'].includes(row.paymentMethod) && isNullableDate(row.cashReceivedAt)
  && typeof row.paymentRecorded === 'boolean' && (row.receipt == null || isContributionReceipt(row.receipt))
  && Array.isArray(row.actions) && row.actions.every(action => typeof action === 'string')
  && isMoney(row.refundedAmount) && Array.isArray(row.adjustments) && row.adjustments.length <= 50 && uniqueContributionRows(row.adjustments) && row.adjustments.every(adjustment)
  && Array.isArray(row.adjustmentActions) && row.adjustmentActions.every(action => typeof action === 'string')
  && (row.cashDeposit === undefined || row.cashDeposit === null || isDeposit(row.cashDeposit))
  && (row.receivedPayment == null || isContributionPayment(row.receivedPayment))
  && (row.evidence == null || isContributionEvidence(row.evidence)) && (row.history === undefined || isContributionHistory(row.history))
  && contributionOptional(row, 'cashDepositId transactionId replacementId correctionOf supersededTransactionId', isText)
  && contributionOptional(row, 'correctionNotice', value => typeof value === 'string') && contributionOptional(row, 'correctedAt', isDate);
const page = payload => isRecord(payload) && Array.isArray(payload.data) && payload.data.length <= 20 && uniqueContributionRows(payload.data) && isCursor(payload.nextCursor);
const isDeposit = row => isRecord(row) && isText(row.id) && isText(row.fundId) && isText(row.fundName)
  && contributionCurrency(row.currency) && isMoney(row.amount) && isText(row.reference) && isDate(row.depositedAt)
  && contributionRevision(row.receiptCount) && isText(row.bankTransactionId)
  && ['reconciled', 'awaiting_reconciliation'].includes(row.status);
export const readContributionOptionsResponse = payload => {
  if (!isRecord(payload) || typeof payload.eligible !== 'boolean' || typeof payload.recipientName !== 'string' || !Array.isArray(payload.funds) || !uniqueContributionRows(payload.funds)
    || !payload.funds.every(fund => isRecord(fund) && isText(fund.id) && isText(fund.name) && contributionCurrency(fund.currency)
      && typeof fund.enabled === 'boolean' && contributionRevision(fund.revision) && typeof fund.instructions === 'string'
      && (fund.externalUrl == null || validExternalUrl(fund.externalUrl)) && (fund.contactEmail == null || typeof fund.contactEmail === 'string')
      && (fund.referenceHint == null || typeof fund.referenceHint === 'string')
      && (fund.bankConnected === undefined || typeof fund.bankConnected === 'boolean')
      && (fund.cashBookOpenedAt === undefined || isNullableDate(fund.cashBookOpenedAt))
      && (fund.cashBookRevision === undefined || contributionRevision(fund.cashBookRevision))
      && (fund.cashOnHand === undefined || fund.cashOnHand === null || isMoney(fund.cashOnHand))
      && (fund.cashAsOf === undefined || isNullableDate(fund.cashAsOf)))) fail('expected permission-filtered fund contribution settings.');
  return payload;
};
export const readContributorLookupResponse = payload => {
  const row = payload?.data;
  if (!isRecord(row) || !isText(row.id) || !isText(row.name) || !isText(row.revision) || !isDate(row.revision) || typeof row.personalHistoryAvailable !== 'boolean') fail('expected a verified contributor identity and revision.');
  return payload;
};
export const readContributionsResponse = payload => {
  if (!page(payload) || !payload.data.every(contribution)) fail('expected contribution records and a nullable page cursor.');
  return payload;
};
export const readContributionDetailResponse = payload => {
  if (!isRecord(payload) || !contribution(payload.data)) fail('expected a permission-filtered contribution record.');
  return payload;
};
export const readContributionPaymentsResponse = payload => {
  if (!page(payload) || !payload.data.every(row => isRecord(row) && isText(row.id) && isMoney(row.amount)
    && isText(row.reference) && isDate(row.date) && (row.statementReference === undefined || typeof row.statementReference === 'string')
    && (row.current === undefined || typeof row.current === 'boolean'))) fail('expected verified incoming payments and a nullable page cursor.');
  return payload;
};
export const readCashDepositsResponse = payload => {
  if (!page(payload) || !payload.data.every(isDeposit)) fail('expected cash deposit records and a nullable page cursor.');
  return payload;
};
export const readContributionReceiptExportResponse = payload => {
  const row = payload?.data; const receipt = row?.receipt;
  if (!contributionPublicRecord(row) || !isText(row.id) || !['confirmed', 'superseded'].includes(row.status) || !isContributionReceipt(receipt)
    || !['number', 'recipientName', 'fundName', 'contributorName', 'currency', 'receivedOn', 'method'].every(key => isText(receipt[key]))
    || !isMoney(receipt.amount) || !isDate(receipt.issuedAt) || typeof receipt.note !== 'string'
    || !isMoney(row.refundedAmount) || !contributionOptional(row, 'correctedAt', isDate) || typeof row.isCorrection !== 'boolean' || !isDate(row.checkedAt)
    || !Array.isArray(row.adjustments) || row.adjustments.length > 50 || !row.adjustments.every(publicAdjustment)) fail('expected a current issued receipt and its published adjustments.');
  return payload;
};
export const readContributionRecoveryContextResponse = payload => {
  if (!isRecord(payload?.data) || typeof payload.data.scope !== 'string' || !/^[a-f\d]{24}_[a-f\d]{24}$/i.test(payload.data.scope)) {
    throw new ContributionRecoveryContextResponseContractError();
  }
  return payload;
};
export const readContributionRecoveryResponse = payload => {
  if (!isRecord(payload) || !['recorded', 'not_recorded'].includes(payload.outcome)) throw new ContributionRecoveryResponseContractError();
  if (payload.outcome === 'not_recorded') {
    if (payload.data !== null) throw new ContributionRecoveryResponseContractError();
  } else if (payload.data !== null && !contribution(payload.data)) throw new ContributionRecoveryResponseContractError();
  return payload;
};
const contributionActions = new Set(['declare', 'record_offline', 'record_cash', 'record_payment', 'link_payment', 'unlink_payment', 'confirm', 'withdraw',
  'propose_correction', 'approve_correction', 'cancel_correction', 'propose_refund', 'propose_clarification', 'approve_adjustment', 'cancel_adjustment', 'record_refund']);
export const readContributionActionResponse = (action, payload) => {
  const row = payload?.data;
  const valid = contributionActions.has(action) ? contribution(row)
    : action === 'configure' ? isRecord(row) && isText(row.fundId) && contributionRevision(row.revision) && typeof row.enabled === 'boolean'
      && typeof row.instructions === 'string' && (row.referenceHint == null || typeof row.referenceHint === 'string')
      && (row.contactEmail == null || typeof row.contactEmail === 'string')
      : action === 'open_cash_book' ? isRecord(row) && isText(row.fundId) && isDate(row.openedAt)
        && row.cashOnHand === 0 && contributionRevision(row.cashBookRevision)
        : action === 'deposit_cash' ? isDeposit(row) : false;
  if (!valid || typeof row.replayed !== 'undefined' && typeof row.replayed !== 'boolean') throw new ContributionActionResponseContractError(action);
  return payload;
};
